import { useEffect, useState } from "react";
import { Trophy, X, Sparkles } from "lucide-react";

/**
 * AchievementUnlockToast — animated per-trophy pop-up.
 * Dashboard polls /api/achievements/inbox after every SSE `done` event; each
 * unread trophy is passed here as a queue item. This component slides one in
 * from the top-right, holds ~5s (or until dismissed), then slides out and
 * calls onDone so the next trophy in the queue can render.
 */

const TIER_COLORS = {
  bronze: { border: "#a97142", glow: "rgba(169,113,66,0.55)",  bg: "linear-gradient(180deg, rgba(60,40,20,0.95), rgba(20,12,5,0.98))" },
  silver: { border: "#c0c5cd", glow: "rgba(192,197,205,0.55)", bg: "linear-gradient(180deg, rgba(45,55,70,0.95), rgba(15,20,25,0.98))" },
  gold:   { border: "#d4af37", glow: "rgba(212,175,55,0.65)",  bg: "linear-gradient(180deg, rgba(60,45,10,0.95), rgba(20,15,3,0.98))" },
  mythic: { border: "#c15fbc", glow: "rgba(193,95,188,0.65)",  bg: "linear-gradient(180deg, rgba(55,20,60,0.95), rgba(20,5,25,0.98))" },
};

export default function AchievementUnlockToast({ trophy, onDone }) {
  const [phase, setPhase] = useState("enter"); // enter | hold | exit
  useEffect(() => {
    const t1 = setTimeout(() => setPhase("hold"), 60);
    const t2 = setTimeout(() => setPhase("exit"), 5200);
    const t3 = setTimeout(() => onDone?.(), 5600);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onDone]);

  if (!trophy) return null;
  const c = TIER_COLORS[trophy.tier || "bronze"] || TIER_COLORS.bronze;
  const enter = phase === "enter";
  const exit  = phase === "exit";

  return (
    <div
      className="fixed z-[80] pointer-events-auto"
      data-testid="achievement-toast"
      style={{
        top: 16,
        right: 16,
        maxWidth: 360,
        transform: enter ? "translateX(120%)" : exit ? "translateX(120%)" : "translateX(0)",
        opacity: enter || exit ? 0 : 1,
        transition: "transform 420ms cubic-bezier(0.20,0.90,0.30,1.05), opacity 420ms ease",
      }}
    >
      <div
        className="panel px-4 py-3 relative overflow-hidden flex items-start gap-3 border-2"
        style={{
          borderColor: c.border,
          boxShadow: `0 0 40px ${c.glow}`,
          background: c.bg,
          minWidth: 300,
        }}
      >
        {/* Ember halo */}
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            background: `radial-gradient(80px 30px at 20% 50%, ${c.glow}, transparent 70%)`,
            animation: "trophy-halo 2.4s ease-in-out infinite",
          }}
        />
        <div className="relative flex-shrink-0 mt-[2px]">
          <Trophy size={26} style={{ color: c.border }} />
          <Sparkles size={10} className="absolute -top-1 -right-1" style={{ color: c.border, animation: "trophy-sparkle 1.4s ease-in-out infinite" }} />
        </div>
        <div className="relative flex-1 min-w-0">
          <div className="text-[9px] tracking-[0.4em]" style={{ color: c.border, opacity: 0.9 }}>
            — {(trophy.tier || "BRONZE").toUpperCase()} · UNLOCKED —
          </div>
          <div className="font-heading text-lg text-[#f0e6d2] leading-tight mt-[2px]">
            {trophy.title}
          </div>
          <div className="text-[11px] ash-text italic mt-1 line-clamp-2">
            {trophy.desc}
          </div>
          <div className="text-[10px] tracking-widest gold-text mt-2">
            +{trophy.reward} SOULS
          </div>
        </div>
        <button
          onClick={() => setPhase("exit")}
          className="relative text-[#8b7965] hover:text-[#f0e6d2] transition-colors flex-shrink-0"
          data-testid="achievement-toast-close"
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>

        <style>{`
          @keyframes trophy-halo {
            0%,100% { opacity: 0.30; }
            50%     { opacity: 0.65; }
          }
          @keyframes trophy-sparkle {
            0%,100% { opacity: 0.4; transform: scale(0.85); }
            50%     { opacity: 1;   transform: scale(1.15); }
          }
        `}</style>
      </div>
    </div>
  );
}
